import { useCallback, useEffect, useRef, useState } from "react";
import {
  BACKGROUND_TRACKS,
  SCENE_CATALOG,
  SFX_CATALOG,
  type AudioScene,
  type AudioVariant,
  type BackgroundDefinition,
  type BackgroundTrackId,
  type SfxId,
} from "../audio/catalog";

export type { AudioScene, SfxId } from "../audio/catalog";

const CROSSFADE_SECONDS = 0.8;
const DUCKED_BACKGROUND_LEVEL = 0.35;
const DUCK_ATTACK_SECONDS = 0.15;
const DUCK_RELEASE_SECONDS = 0.6;
const MAX_SEEN_EVENT_KEYS = 512;

const ENABLED_STORAGE_KEY = "ma-soi:sound";
const BACKGROUND_VOLUME_STORAGE_KEY = "ma-soi:background-volume";
const SFX_VOLUME_STORAGE_KEY = "ma-soi:sfx-volume";

interface AudioGraph {
  context: AudioContext;
  music: GainNode;
  ambience: GainNode;
  sfx: GainNode;
}

interface PlayingBackground {
  id: BackgroundTrackId;
  source: AudioBufferSourceNode;
  gain: GainNode;
}

type BackgroundBus = "music" | "ambience";

function clampVolume(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function readStorage(key: string) {
  try {
    return typeof window === "undefined" ? null : window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Sound preferences are best-effort when storage is blocked.
  }
}

function readStoredVolume(key: string, fallback: number) {
  const stored = readStorage(key);
  if (stored === null) return fallback;
  const parsed = Number(stored);
  return Number.isFinite(parsed) ? clampVolume(parsed) : fallback;
}

function rampGain(graph: AudioGraph, gain: GainNode, target: number, durationSeconds: number) {
  if (graph.context.state === "closed") return;
  const now = graph.context.currentTime;
  gain.gain.cancelScheduledValues(now);
  gain.gain.setValueAtTime(gain.gain.value, now);
  if (durationSeconds <= 0) gain.gain.setValueAtTime(target, now);
  else gain.gain.linearRampToValueAtTime(target, now + durationSeconds);
}

function orderedSources(variant: AudioVariant) {
  if (typeof document === "undefined") return variant.sources;
  const probe = document.createElement("audio");
  const supported = variant.sources.filter((source) => probe.canPlayType(source.type) !== "");
  const unknown = variant.sources.filter((source) => probe.canPlayType(source.type) === "");
  return supported.length > 0 ? [...supported, ...unknown] : variant.sources;
}

function variantCacheKey(variant: AudioVariant) {
  return variant.sources.map((source) => source.src).join("|");
}

function backgroundTarget(volume: number, ducked: boolean) {
  return volume * (ducked ? DUCKED_BACKGROUND_LEVEL : 1);
}

export function useSound() {
  const [enabled, setEnabled] = useState(() => readStorage(ENABLED_STORAGE_KEY) === "on");
  const [backgroundVolume, setBackgroundVolumeState] = useState(() => (
    readStoredVolume(BACKGROUND_VOLUME_STORAGE_KEY, 0.18)
  ));
  const [sfxVolume, setSfxVolumeState] = useState(() => readStoredVolume(SFX_VOLUME_STORAGE_KEY, 0.45));
  const [voiceDucking, setVoiceDuckingState] = useState(false);
  const [currentScene, setCurrentScene] = useState<AudioScene>("home");

  const enabledRef = useRef(enabled);
  const backgroundVolumeRef = useRef(backgroundVolume);
  const sfxVolumeRef = useRef(sfxVolume);
  const voiceDuckingRef = useRef(voiceDucking);
  const sceneRef = useRef<AudioScene>(currentScene);
  const graphRef = useRef<AudioGraph | null>(null);
  const buffersRef = useRef(new Map<string, Promise<AudioBuffer | null>>());
  const backgroundRef = useRef<Record<BackgroundBus, PlayingBackground | null>>({
    music: null,
    ambience: null,
  });
  const activeSfxRef = useRef(new Set<AudioBufferSourceNode>());
  const seenEventKeysRef = useRef(new Map<string, true>());
  const cooldownsRef = useRef(new Map<SfxId, number>());
  const lastVariantRef = useRef(new Map<SfxId, number>());
  const lastSceneKeyRef = useRef<string | null>(null);
  const sceneGenerationRef = useRef(0);
  const mountedRef = useRef(true);

  const ensureGraph = useCallback((): AudioGraph | null => {
    const existing = graphRef.current;
    if (existing && existing.context.state !== "closed") return existing;
    if (typeof window === "undefined") return null;

    const ContextConstructor = globalThis.AudioContext
      ?? (globalThis as typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!ContextConstructor) return null;

    const context = new ContextConstructor();
    const music = context.createGain();
    const ambience = context.createGain();
    const sfx = context.createGain();
    const baseBackground = backgroundTarget(backgroundVolumeRef.current, voiceDuckingRef.current);
    music.gain.value = baseBackground;
    ambience.gain.value = baseBackground;
    sfx.gain.value = sfxVolumeRef.current;
    music.connect(context.destination);
    ambience.connect(context.destination);
    sfx.connect(context.destination);

    const graph = { context, music, ambience, sfx };
    graphRef.current = graph;
    return graph;
  }, []);

  const loadBuffer = useCallback((graph: AudioGraph, variant: AudioVariant) => {
    const cacheKey = variantCacheKey(variant);
    const existing = buffersRef.current.get(cacheKey);
    if (existing) return existing;

    const pending = (async (): Promise<AudioBuffer | null> => {
      for (const source of orderedSources(variant)) {
        try {
          const response = await fetch(source.src, { cache: "force-cache" });
          if (!response.ok) continue;
          const bytes = await response.arrayBuffer();
          return await graph.context.decodeAudioData(bytes.slice(0));
        } catch {
          // Try the next codec. Missing or invalid audio never breaks gameplay.
        }
      }
      return null;
    })();

    buffersRef.current.set(cacheKey, pending);
    void pending.then((buffer) => {
      if (!buffer && buffersRef.current.get(cacheKey) === pending) buffersRef.current.delete(cacheKey);
    });
    return pending;
  }, []);

  const loadBackground = useCallback((graph: AudioGraph, id: BackgroundTrackId | undefined) => {
    if (!id) return Promise.resolve<AudioBuffer | null>(null);
    const definition = BACKGROUND_TRACKS[id];
    const variant = definition.variants[0];
    return variant ? loadBuffer(graph, variant) : Promise.resolve<AudioBuffer | null>(null);
  }, [loadBuffer]);

  const fadeOutBackground = useCallback((graph: AudioGraph, track: PlayingBackground, duration = CROSSFADE_SECONDS) => {
    if (graph.context.state === "closed") return;
    const now = graph.context.currentTime;
    track.gain.gain.cancelScheduledValues(now);
    track.gain.gain.setValueAtTime(track.gain.gain.value, now);
    track.gain.gain.linearRampToValueAtTime(0, now + duration);
    try {
      track.source.stop(now + duration + 0.02);
    } catch {
      // A source that already ended needs no further cleanup.
    }
  }, []);

  const stopAll = useCallback((immediate = false) => {
    sceneGenerationRef.current += 1;
    const graph = graphRef.current;
    if (graph) {
      for (const bus of ["music", "ambience"] as const) {
        const track = backgroundRef.current[bus];
        if (!track) continue;
        if (immediate) {
          try {
            track.source.stop();
          } catch {
            // The track may already have stopped.
          }
        } else {
          fadeOutBackground(graph, track, 0.08);
        }
        backgroundRef.current[bus] = null;
      }
    }

    for (const source of activeSfxRef.current) {
      try {
        source.stop();
      } catch {
        // The sound may already have stopped.
      }
    }
    activeSfxRef.current.clear();
  }, [fadeOutBackground]);

  const transitionScene = useCallback(async (scene: AudioScene) => {
    if (!enabledRef.current || !mountedRef.current) return;
    const graph = ensureGraph();
    if (!graph) return;

    // resume() must be attempted synchronously from user-triggered callers. When
    // autoplay blocks it, sources remain queued on the suspended context.
    if (graph.context.state === "suspended") void graph.context.resume().catch(() => undefined);

    const generation = ++sceneGenerationRef.current;
    const definition = SCENE_CATALOG[scene];
    const [musicBuffer, ambienceBuffer] = await Promise.all([
      loadBackground(graph, definition.music),
      loadBackground(graph, definition.ambience),
    ]);

    if (
      generation !== sceneGenerationRef.current
      || !enabledRef.current
      || !mountedRef.current
      || graphRef.current !== graph
      || graph.context.state === "closed"
    ) return;

    const replaceTrack = (
      bus: BackgroundBus,
      id: BackgroundTrackId | undefined,
      buffer: AudioBuffer | null,
    ) => {
      const previous = backgroundRef.current[bus];
      if (previous?.id === id) return;

      if (!id || !buffer) {
        if (previous) fadeOutBackground(graph, previous);
        backgroundRef.current[bus] = null;
        return;
      }

      const trackDefinition: BackgroundDefinition = BACKGROUND_TRACKS[id];
      const source = graph.context.createBufferSource();
      const gain = graph.context.createGain();
      const now = graph.context.currentTime;
      source.buffer = buffer;
      source.loop = trackDefinition.loop;
      if (trackDefinition.loopStart !== undefined) source.loopStart = trackDefinition.loopStart;
      if (trackDefinition.loopEnd !== undefined) source.loopEnd = trackDefinition.loopEnd;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(trackDefinition.gain, now + CROSSFADE_SECONDS);
      source.connect(gain).connect(graph[bus]);

      const playing = { id, source, gain };
      source.onended = () => {
        gain.disconnect();
        if (backgroundRef.current[bus]?.source === source) backgroundRef.current[bus] = null;
      };

      try {
        source.start(now);
      } catch {
        gain.disconnect();
        return;
      }

      backgroundRef.current[bus] = playing;
      if (previous) fadeOutBackground(graph, previous);
    };

    replaceTrack("music", definition.music, musicBuffer);
    replaceTrack("ambience", definition.ambience, ambienceBuffer);

    const preloadIds = new Set<BackgroundTrackId>();
    for (const preloadScene of definition.preload) {
      const next = SCENE_CATALOG[preloadScene];
      if (next.music) preloadIds.add(next.music);
      if (next.ambience) preloadIds.add(next.ambience);
    }
    for (const id of preloadIds) void loadBackground(graph, id);

    const retainedIds = new Set<BackgroundTrackId>(preloadIds);
    if (definition.music) retainedIds.add(definition.music);
    if (definition.ambience) retainedIds.add(definition.ambience);
    for (const [id, track] of Object.entries(BACKGROUND_TRACKS) as [BackgroundTrackId, BackgroundDefinition][]) {
      if (retainedIds.has(id)) continue;
      for (const variant of track.variants) buffersRef.current.delete(variantCacheKey(variant));
    }
  }, [ensureGraph, fadeOutBackground, loadBackground]);

  const playSfx = useCallback((id: SfxId, eventKey?: string) => {
    if (eventKey) {
      const dedupeKey = `${id}:${eventKey}`;
      if (seenEventKeysRef.current.has(dedupeKey)) return;
      seenEventKeysRef.current.set(dedupeKey, true);
      if (seenEventKeysRef.current.size > MAX_SEEN_EVENT_KEYS) {
        const oldest = seenEventKeysRef.current.keys().next().value;
        if (oldest) seenEventKeysRef.current.delete(oldest);
      }
    }
    if (!enabledRef.current || !mountedRef.current) return;

    const definition = SFX_CATALOG[id];
    const nowMs = Date.now();
    const lastPlayedAt = cooldownsRef.current.get(id) ?? 0;
    if (definition.cooldownMs && nowMs - lastPlayedAt < definition.cooldownMs) return;
    cooldownsRef.current.set(id, nowMs);

    const graph = ensureGraph();
    if (!graph) return;
    if (graph.context.state === "suspended") void graph.context.resume().catch(() => undefined);

    const variantCount = definition.variants.length;
    const previousVariant = lastVariantRef.current.get(id) ?? -1;
    const variantIndex = variantCount <= 1
      ? 0
      : (previousVariant + 1 + Math.floor(Math.random() * (variantCount - 1))) % variantCount;
    const variant = definition.variants[variantIndex] ?? definition.variants[0];
    if (!variant) return;
    lastVariantRef.current.set(id, variantIndex);
    const generation = sceneGenerationRef.current;

    void loadBuffer(graph, variant).then((buffer) => {
      if (
        !buffer
        || generation !== sceneGenerationRef.current
        || !enabledRef.current
        || !mountedRef.current
        || graphRef.current !== graph
        || graph.context.state === "closed"
      ) return;

      const source = graph.context.createBufferSource();
      const gain = graph.context.createGain();
      source.buffer = buffer;
      gain.gain.value = definition.gain;
      source.connect(gain).connect(graph.sfx);
      activeSfxRef.current.add(source);
      source.onended = () => {
        activeSfxRef.current.delete(source);
        gain.disconnect();
      };
      try {
        source.start();
      } catch {
        activeSfxRef.current.delete(source);
        gain.disconnect();
      }
    });
  }, [ensureGraph, loadBuffer]);

  const setScene = useCallback((scene: AudioScene, sceneKey?: string) => {
    if (sceneKey) {
      const dedupeKey = `${scene}:${sceneKey}`;
      if (lastSceneKeyRef.current === dedupeKey) return;
      lastSceneKeyRef.current = dedupeKey;
    } else if (sceneRef.current === scene) {
      return;
    }

    sceneRef.current = scene;
    setCurrentScene(scene);
    void transitionScene(scene);
  }, [transitionScene]);

  const setBackgroundVolume = useCallback((value: number) => {
    const next = clampVolume(value);
    backgroundVolumeRef.current = next;
    setBackgroundVolumeState(next);
    writeStorage(BACKGROUND_VOLUME_STORAGE_KEY, String(next));
    const graph = graphRef.current;
    if (!graph) return;
    const target = backgroundTarget(next, voiceDuckingRef.current);
    rampGain(graph, graph.music, target, 0.05);
    rampGain(graph, graph.ambience, target, 0.05);
  }, []);

  const setSfxVolume = useCallback((value: number) => {
    const next = clampVolume(value);
    sfxVolumeRef.current = next;
    setSfxVolumeState(next);
    writeStorage(SFX_VOLUME_STORAGE_KEY, String(next));
    const graph = graphRef.current;
    if (graph) rampGain(graph, graph.sfx, next, 0.05);
  }, []);

  const setVoiceDucking = useCallback((ducked: boolean) => {
    voiceDuckingRef.current = ducked;
    setVoiceDuckingState(ducked);
    const graph = graphRef.current;
    if (!graph) return;
    const target = backgroundTarget(backgroundVolumeRef.current, ducked);
    const duration = ducked ? DUCK_ATTACK_SECONDS : DUCK_RELEASE_SECONDS;
    rampGain(graph, graph.music, target, duration);
    rampGain(graph, graph.ambience, target, duration);
  }, []);

  const toggle = useCallback(async () => {
    if (enabledRef.current) {
      enabledRef.current = false;
      setEnabled(false);
      writeStorage(ENABLED_STORAGE_KEY, "off");
      stopAll(true);
      const context = graphRef.current?.context;
      if (context?.state === "running") await context.suspend().catch(() => undefined);
      return;
    }

    enabledRef.current = true;
    setEnabled(true);
    writeStorage(ENABLED_STORAGE_KEY, "on");
    const graph = ensureGraph();
    if (graph?.context.state === "suspended") await graph.context.resume().catch(() => undefined);
    void transitionScene(sceneRef.current);
    playSfx("sound_on");
  }, [ensureGraph, playSfx, stopAll, transitionScene]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopAll(true);
      const context = graphRef.current?.context;
      graphRef.current = null;
      buffersRef.current.clear();
      if (context && context.state !== "closed") void context.close();
    };
  }, [stopAll]);

  useEffect(() => {
    if (enabledRef.current) void transitionScene(sceneRef.current);
  }, [transitionScene]);

  useEffect(() => {
    if (!enabled || currentScene !== "night") return;
    let timer = 0;
    const schedule = (delay = 45_000 + Math.random() * 45_000) => {
      timer = window.setTimeout(() => {
        if (voiceDuckingRef.current) schedule(5_000);
        else {
          playSfx("wolf_howl");
          schedule();
        }
      }, delay);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [currentScene, enabled, playSfx]);

  return {
    enabled,
    toggle,
    playSfx,
    setScene,
    backgroundVolume,
    setBackgroundVolume,
    sfxVolume,
    setSfxVolume,
    voiceDucking,
    setVoiceDucking,
  };
}
